import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Box,
  Button,
  DataTable,
  DataTableCellVariants,
  EmptyState,
  Highlight,
  Icon,
  Illustration,
  Pagination,
  RowsSkeletonLoader,
  useSidePanel,
  type DataTableHeaderProps,
  type DataTableRowData,
} from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useGetPaymentLimits } from 'hooks/queries/useGetPaymentLimits'
import { useSearchablePagination } from 'hooks/useSearchablePagination'
import { PaymentLimitSheet } from 'pages/PaymentLimits/components/PaymentLimitSheet'
import { formatPaymentMethodLabel, LIMIT_TYPE_LABELS } from 'pages/PaymentLimits/utils'
import { PaymentLimitSheetType, type Limit } from 'types/paymentLimits'
import { roundNumberWithCommas } from 'utils/utils'
import { CustomerPaymentLimitSidePanel } from './CustomerPaymentLimitSidePanel'

interface CustomerPaymentLimitsTableProps {
  customerReference: string
}

export const CustomerPaymentLimitsTable = ({
  customerReference,
}: CustomerPaymentLimitsTableProps) => {
  const { t } = useTranslation()
  const { currentAccount } = useAccountsContext()
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useSearchablePagination(MIN_PAGINATION_SIZE)

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false)
  const [selectedLimit, setSelectedLimit] = useState<Limit | null>(null)

  const { data: limitsData, isFetching } = useGetPaymentLimits({
    accountId: currentAccount?.reference,
    customerId: customerReference,
    pageNumber: page,
    pageSize: rowsPerPage,
  })

  const limits = limitsData?.content ?? []
  const totalElements = limitsData?.totalElements ?? 0

  const hasLimits = limits?.length > 0

  const {
    isSidePanelOpen,
    sidePanelData: selectedSidePanelLimit,
    paging: sidePanelPaging,
    setIsSidePanelOpen,
    updateSidePanel,
  } = useSidePanel(limits)

  const openSidePanel = (index: number) => {
    updateSidePanel(index)
    setIsSidePanelOpen(true)
  }

  const handleEditLimit = (limit: Limit) => {
    setSelectedLimit(limit)
    setIsEditSheetOpen(true)
  }

  const handleCloseSheet = () => {
    setIsEditSheetOpen(false)
    setSelectedLimit(null)
  }

  const columns: DataTableHeaderProps[] = useMemo(
    () => [
      {
        key: 'paymentMethod',
        label: t('customers.paymentLimitsTable.paymentMethod'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'limitType',
        label: t('customers.paymentLimitsTable.limitType'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'amount',
        label: t('customers.paymentLimitsTable.amount'),
        variant: DataTableCellVariants.TEXT,
        className: 'text-right',
      },
      {
        key: 'currency',
        label: t('customers.paymentLimitsTable.currency'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'actions',
        label: '',
        variant: DataTableCellVariants.ACTION,
      },
    ],
    [t]
  )

  const tableData: DataTableRowData[] = useMemo(() => {
    if (!limits?.length) return []

    return limits.map((limit, index) => ({
      id: limit?.ruleId ?? `limit-${index}`,
      testid: limit?.ruleId,
      onRowClick: () => openSidePanel(index),
      cells: [
        {
          key: 'paymentMethod',
          variant: DataTableCellVariants.TEXT,
          Text: formatPaymentMethodLabel(limit.method ?? limit.qualifiers?.method ?? ''),
        },
        {
          key: 'limitType',
          variant: DataTableCellVariants.HIGHLIGHT,
          Highlights: [
            <Highlight
              key={`${limit.ruleId}-limitType`}
              type={limit.limitType === 'DAILY' ? 'feature' : 'neutral'}
              variant="emphasis"
            >
              {t(LIMIT_TYPE_LABELS[limit.limitType] ?? limit.limitType)}
            </Highlight>,
          ],
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: roundNumberWithCommas(limit.amount, 2),
          className: 'text-right',
        },
        {
          key: 'currency',
          variant: DataTableCellVariants.TEXT,
          Text: (
            <Box flex align="center" gap={8}>
              <Avatar name={limit.currency} type="currency" size="small" />
              <Box className="text-body-regular text-foreground-prominent">{limit.currency}</Box>
            </Box>
          ),
        },
        {
          key: 'actions',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            <Button
              key={`edit-${limit.ruleId}`}
              variant="ghost"
              noPadding
              iconElement={<Icon name="SimplePencilCustom" />}
              onClick={e => {
                e.stopPropagation()
                handleEditLimit(limit)
              }}
              testId={`edit-limit-${limit.ruleId}`}
            />,
          ],
        },
      ],
    }))
  }, [limits])

  if (isFetching) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (!hasLimits) {
    return (
      <>
        <EmptyState
          testId="customer-payment-limits-empty-state"
          LottiePlayer={<Illustration name="Welcome" />}
          title={t('customers.paymentLimitsTable.emptyStateTitle')}
          description={t('customers.paymentLimitsTable.emptyStateDescription')}
          PrimaryButton={
            <Button
              testId="add-customer-limit-button"
              variant="secondary"
              iconElement={<Icon name="Plus" />}
              onClick={() => setIsAddSheetOpen(true)}
            >
              {t('paymentLimits.addLimit')}
            </Button>
          }
        />

        <PaymentLimitSheet
          isOpen={isAddSheetOpen}
          type={PaymentLimitSheetType.ADD}
          customerId={customerReference}
          onClose={() => setIsAddSheetOpen(false)}
        />
      </>
    )
  }

  return (
    <>
      <Box flex justify="end" paddingB={16}>
        <Button
          testId="add-customer-limit-button"
          variant="secondary"
          iconElement={<Icon name="Plus" />}
          onClick={() => setIsAddSheetOpen(true)}
        >
          {t('paymentLimits.addLimit')}
        </Button>
      </Box>

      <DataTable name="CustomerPaymentLimitsTable" headers={columns} data={tableData} />

      {totalElements > rowsPerPage && (
        <Box paddingT={16}>
          <Pagination
            testId="customer-payment-limits-pagination"
            page={page}
            rowsPerPage={rowsPerPage}
            total={totalElements}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </Box>
      )}

      <PaymentLimitSheet
        isOpen={isAddSheetOpen}
        type={PaymentLimitSheetType.ADD}
        customerId={customerReference}
        onClose={() => setIsAddSheetOpen(false)}
      />

      <PaymentLimitSheet
        isOpen={isEditSheetOpen}
        type={PaymentLimitSheetType.EDIT}
        limit={selectedLimit}
        customerId={customerReference}
        onClose={handleCloseSheet}
      />

      <CustomerPaymentLimitSidePanel
        isOpen={isSidePanelOpen}
        limit={selectedSidePanelLimit ?? null}
        paging={sidePanelPaging}
        onClose={() => setIsSidePanelOpen(false)}
      />
    </>
  )
}
